import { useState, useEffect } from 'react';

export const useLocalStorage = (keyName: string, defaultValue: any = undefined) => {
  const [storedValue, setStoredValue] = useState(() => {
    // CRITICAL FIX: Always return default value during SSR and initial client render
    // This prevents hydration mismatches that can cause infinite loading
    return defaultValue;
  });
  
  const [isHydrated, setIsHydrated] = useState(false);

  // CRITICAL FIX: Hydrate from localStorage after component mounts
  // Only update state once, after hydration is complete
  useEffect(() => {
    if (typeof window !== 'undefined' && !isHydrated) {
      try {
        const value = window.localStorage.getItem(keyName);
        if (value !== null) {
          try {
            const parsedValue = JSON.parse(value);
            setStoredValue(parsedValue);
          } catch (error) {
            setStoredValue(value);
          }
        } else if (defaultValue !== undefined) {
          // Set default value in localStorage if it doesn't exist
          window.localStorage.setItem(keyName, JSON.stringify(defaultValue));
          setStoredValue(defaultValue);
        }
      } catch (err) {
        // localStorage access failed, keep default value
        console.warn(`localStorage access failed for key ${keyName}:`, err);
      } finally {
        setIsHydrated(true);
      }
    }
  }, [keyName, defaultValue, isHydrated]);

  const setValue = (newValue: any) => {
    // Only update if we're in a browser environment
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(
          keyName,
          typeof newValue === 'string' ? newValue : JSON.stringify(newValue),
        );
      } catch (err) {
        console.warn(`Failed to set localStorage for key ${keyName}:`, err);
      }
    }
    setStoredValue(newValue);
  };

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === keyName && e.newValue !== null) {
        try {
          // Try to parse as JSON first
          const newValue = JSON.parse(e.newValue);
          setStoredValue(newValue);
        } catch {
          // If parsing fails, use the raw value
          setStoredValue(e.newValue);
        }
      } else if (e.key === keyName && e.newValue === null) {
        // Key was removed
        setStoredValue(defaultValue);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [keyName, defaultValue]);

  return [storedValue, setValue];
};
